import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Select, Table, Typography, Button,
  message, Empty, theme, Segmented, Alert,
} from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import {
  EyeOutlined,
  ShareAltOutlined,
  TrophyOutlined,
  RiseOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  FileSearchOutlined,
  FilePdfOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { analyticsService, eventoService, catalogoGeneticaService } from '../../services/api';
import MetricasCatalogo from './MetricasCatalogo';
import { descargarBlob } from '../../utils/descargas';
import { eventosDeListaCorta } from '../../utils/listaCorta';
import LineChart from '../../components/LineChart';

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

// Mismo criterio de texto que `NIVEL_ANALYTICS` en Invitaciones.jsx —
// consistencia entre las dos pantallas donde se explica qué decidió
// compartir el organizador, no un mensaje nuevo que confunda.
const AVISO_INVITADO = {
  agregado: (organizador) =>
    `Estás viendo las métricas que compartió ${organizador}: los totales son del `
    + 'remate completo, el detalle por animal es sólo de los tuyos.',
  total: (organizador) =>
    `Estás viendo las métricas que compartió ${organizador}: acceso completo al `
    + 'detalle del remate.',
};

const DIAS_OPTIONS = [
  { label: '7 días', value: 7 },
  { label: '30 días', value: 30 },
  { label: '60 días', value: 60 },
  { label: '90 días', value: 90 },
  { label: '1 año', value: 365 },
  { label: 'Todo', value: 0 },
];



// Botón flotado a la derecha del título de cada Card (prop `extra` de antd
// Card = misma fila que el title). Abre la página con el listado completo
// (sin el top-20 del tablero) + exportar Excel/PDF.
// A NIVEL DE MÓDULO a propósito — si vive dentro del render de Analytics,
// cada render (cambio de días, de evento, cada fetch) crea una función con
// identidad nueva y React la trata como un componente distinto: desmonta y
// remonta los 5 botones. Mismo motivo por el que ImageOption/ColorField
// viven afuera en otras pantallas del panel.
const VerCompletoBtn = ({ tipo, eventoId, nombreEvento, navigate }) => (
  <Button
    size="small"
    type="link"
    icon={<FileSearchOutlined />}
    onClick={() => navigate(
      `/analytics/completo/${tipo}?evento=${eventoId}`,
      { state: { nombreEvento } },
    )}
  >
    Ver estadística completa
  </Button>
);

// ── Componente principal ──────────────────────────────────────────────────────
const Analytics = () => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState(null);
  // LOS CATALOGOS TAMBIEN SE MIRAN ACA. Un catalogo no es un remate: no tiene
  // fecha, no termina y no tiene ofertas. Pero los numeros se miran en UN solo
  // lugar — dos pantallas para lo mismo obligan al productor a recordar cual
  // abrir segun que quiera ver.
  const [catalogos, setCatalogos] = useState([]);
  // Que hay elegido: `{ tipo: 'evento' | 'catalogo', id }`. Un solo estado y no
  // dos ids, porque son EXCLUYENTES: se mira un remate o se mira un catalogo.
  // Con dos ids habria que acordarse de limpiar el otro en cada cambio.
  const [seleccion, setSeleccion] = useState(null);
  const [dias, setDias] = useState(30);
  const [loading, setLoading] = useState(false);
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [datos, setDatos] = useState(null);
  const [exportandoTablero, setExportandoTablero] = useState(false);
  const { token } = theme.useToken();

  // Descarga el tablero completo (visitas + ventas por categoría) del evento y
  // el rango de días que se están mirando ahora mismo.
  const handleExportarTablero = async () => {
    setExportandoTablero(true);
    try {
      const res = await analyticsService.exportarTableroPdf(eventoId, dias);
      descargarBlob(res, `tablero_${eventoId}.pdf`);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al exportar el tablero');
    } finally {
      setExportandoTablero(false);
    }
  };

  // Lo que se puede mirar: los remates (lista liviana, 6 campos) y los
  // catalogos de genetica.
  useEffect(() => {
    const cargar = async () => {
      // `allSettled` y no `all`: un plan puede tener analytics y NO tener
      // catalogo de genetica, y ahi la segunda llamada vuelve 403. Con `all`
      // ese 403 se lleva puesta tambien la lista de remates y la pantalla queda
      // vacia para alguien que SI tiene remates que mirar.
      const [resEventos, resCatalogos] = await Promise.allSettled([
        eventoService.getListaCorta(),
        catalogoGeneticaService.getAll(),
      ]);

      const listaEventos = resEventos.status === 'fulfilled'
        ? eventosDeListaCorta(resEventos.value) : [];
      const listaCatalogos = resCatalogos.status === 'fulfilled'
        ? (resCatalogos.value.data.catalogos || []) : [];

      setEventos(listaEventos);
      setCatalogos(listaCatalogos);

      if (resEventos.status === 'rejected') {
        message.error(resEventos.reason?.response?.data?.error || 'Error al cargar remates');
      }

      // ARRANCA EN UN REMATE si hay: es lo que esta pantalla mostro siempre, y
      // el remate activo es lo que el productor mira todos los dias. Si no hay
      // remates pero si catalogos, abre en el primer catalogo en vez de dejar
      // una pantalla vacia con cosas para elegir al lado.
      const activo = listaEventos.find((e) => e.estado_evento === 1);
      const inicial = activo || listaEventos[0];
      if (inicial) {
        setEventoId(inicial.id_evento);
        setSeleccion({ tipo: 'evento', id: inicial.id_evento });
      } else if (listaCatalogos.length) {
        setSeleccion({ tipo: 'catalogo', id: listaCatalogos[0].id_catalogo_genetica });
      }
      setLoadingEventos(false);
    };
    cargar();
  }, []);

  // Cargar analytics cuando cambia el evento o el rango de días
  const fetchTablero = useCallback(async (id, d) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await analyticsService.getTablero(id, d);
      setDatos(res.data);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar el tablero de analytics');
      setDatos(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // SOLO para un remate. El catalogo tiene su propio endpoint y su propio
    // componente: pedirle el tablero de un evento devolveria 404 y dejaria un
    // mensaje de error sobre una pantalla que en realidad funciona.
    if (seleccion?.tipo === 'evento' && seleccion.id) {
      fetchTablero(seleccion.id, dias);
    }
  }, [seleccion, dias, fetchTablero]);

  // Columnas tablas
  // Los corrales agrupados llegan como UN renglón con `children` (los
  // animales del lote, `nivel` 1): antd dibuja el árbol solo. El renglón del
  // corral trae el nombre ya armado ("Corral 47 · 3 cabezas") en `nom_animal`
  // y la suma en `visitas`; los hijos no se numeran.
  const colsAnimalVisitas = [
    { title: '#', render: (_, r, i) => (r.nivel === 1 ? '' : i + 1), width: 40 },
    {
      title: 'RP', dataIndex: 'rp_animal', key: 'rp_animal', ellipsis: true,
      render: (v, r) => (r.children ? <Text strong>{r.nom_animal}</Text> : (v || '—')),
    },
    { title: 'Corral', dataIndex: 'corral', key: 'corral', width: 80, render: (v) => v || '—' },
    { title: 'Visitas', dataIndex: 'visitas', key: 'visitas', align: 'right', width: 80 },
  ];

  const colsAnimalCompartidos = [
    { title: '#', render: (_, __, i) => i + 1, width: 40 },
    { title: 'RP', dataIndex: 'rp_animal', key: 'rp_animal', ellipsis: true, render: (v) => v || '—' },
    { title: 'Corral', dataIndex: 'corral', key: 'corral', width: 80, render: (v) => v || '—' },
    { title: 'Compartidos', dataIndex: 'compartidos', key: 'compartidos', align: 'right', width: 110 },
  ];

  const colsOfertasAnimal = [
    { title: '#', render: (_, __, i) => i + 1, width: 40 },
    { title: 'Animal', dataIndex: 'nom_animal', key: 'nom_animal', ellipsis: true },
    { title: 'Ofertas', dataIndex: 'ofertas', key: 'ofertas', align: 'right', width: 70 },
    {
      title: 'Máx.',
      dataIndex: 'monto_maximo',
      key: 'monto_maximo',
      align: 'right',
      width: 100,
      render: (v) => v == null ? '—' : `$${Number(v).toLocaleString('es-AR')}`,
    },
  ];

  const colsOfertasCategoria = [
    { title: '#', render: (_, __, i) => i + 1, width: 40 },
    { title: 'Categoría', dataIndex: 'nom_categoria', key: 'nom_categoria', ellipsis: true },
    { title: 'Ofertas', dataIndex: 'ofertas', key: 'ofertas', align: 'right', width: 80 },
  ];

  const colsOfertasCorral = [
    { title: 'Corral', dataIndex: 'corral', key: 'corral', width: 80 },
    { title: 'Ofertas', dataIndex: 'ofertas', key: 'ofertas', align: 'right', width: 80 },
    {
      title: 'Máx.',
      dataIndex: 'monto_maximo',
      key: 'monto_maximo',
      align: 'right',
      render: (v) => v == null ? '—' : `$${Number(v).toLocaleString('es-AR')}`,
    },
  ];

  const tableProps = {
    size: 'small',
    pagination: false,
    scroll: { y: 220 },
    style: { fontSize: 13 },
  };

  const nombreEventoActual = eventos.find((e) => e.id_evento === eventoId)?.nombre_evento || '';

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
        <Col>
          <Row gutter={16} align="middle">
            <Col>
              <Title level={3} style={{ margin: 0 }}>
                <BarChartOutlined style={{ marginRight: 10, color: token.colorPrimary }} />
                Analytics
              </Title>
            </Col>
            {/* EL PDF ES DEL TABLERO DE UN REMATE: trae visitas y ventas por
                categoria, que un catalogo no tiene. Con un catalogo elegido no
                se dibuja, en vez de dejarlo habilitado exportando el PDF del
                ULTIMO remate mirado — que es lo que pasaria, porque `eventoId`
                y `datos` siguen con su valor. */}
            {seleccion?.tipo !== 'catalogo' && (
              <Col>
                <Button
                  icon={<FilePdfOutlined />}
                  loading={exportandoTablero}
                  onClick={handleExportarTablero}
                  disabled={!eventoId || !datos}
                >
                  Exportar tablero (PDF)
                </Button>
              </Col>
            )}
          </Row>
        </Col>
        <Col>
          <Row gutter={12} align="middle">
            <Col>
              <Segmented
                options={DIAS_OPTIONS}
                value={dias}
                onChange={setDias}
              />
            </Col>
            <Col>
              {/* UN SOLO SELECTOR CON DOS GRUPOS, y no una lista plana: un
                  remate y un catalogo no son lo mismo —uno tiene fecha y
                  termina, el otro no— y mezclados no se distingue que es cada
                  cosa. Los `<OptGroup>` lo dicen sin que haya que explicarlo.

                  El `value` es una CLAVE COMPUESTA ("evento:12") porque los ids
                  de las dos tablas pueden coincidir: el evento 3 y el catalogo
                  3 existen a la vez, y con el id pelado el selector no sabria
                  cual marcar. */}
              <Select
                placeholder={loadingEventos ? 'Cargando...' : 'Elegí qué mirar'}
                style={{ minWidth: 280 }}
                value={seleccion ? `${seleccion.tipo}:${seleccion.id}` : undefined}
                onChange={(clave) => {
                  const [tipo, id] = clave.split(':');
                  const idNum = Number(id);
                  setSeleccion({ tipo, id: idNum });
                  // `eventoId` se mantiene para los botones de exportar y el
                  // "Ver completo", que son del remate y siguen leyendolo.
                  if (tipo === 'evento') setEventoId(idNum);
                }}
                disabled={loadingEventos}
                loading={loadingEventos}
                showSearch
                optionFilterProp="children"
              >
                {eventos.length > 0 && (
                  <OptGroup label="Remates">
                    {eventos.map((e) => (
                      <Option key={`evento:${e.id_evento}`} value={`evento:${e.id_evento}`}>
                        {e.nombre_evento}
                      </Option>
                    ))}
                  </OptGroup>
                )}
                {catalogos.length > 0 && (
                  <OptGroup label="Catálogos de genética">
                    {catalogos.map((c) => (
                      <Option
                        key={`catalogo:${c.id_catalogo_genetica}`}
                        value={`catalogo:${c.id_catalogo_genetica}`}
                      >
                        {c.nombre_catalogo}
                      </Option>
                    ))}
                  </OptGroup>
                )}
              </Select>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* UN CATALOGO NO ES UN REMATE CON OTROS DATOS: no tiene ofertas, ni
          ventas por categoria, ni invitados que vean una parte. Por eso es un
          componente aparte y no un `if` adentro del tablero — cruzar las dos
          formas de condicionales deja una pantalla que nadie entiende. */}
      {seleccion?.tipo === 'catalogo' ? (
        <MetricasCatalogo
          idCatalogo={seleccion.id}
          // LA VENTANA LA MANDA EL CONTROL DE ARRIBA, el mismo que usa el
          // tablero del remate. Dos selectores de dias en la misma pantalla
          // —uno global y otro adentro— es justamente la clase de duplicacion
          // que esta mudanza vino a sacar.
          dias={dias}
          encendido={Boolean(
            catalogos.find((c) => c.id_catalogo_genetica === seleccion.id)?.activo,
          )}
        />
      ) : !eventoId ? (
        <Empty description="Elegí un remate o un catálogo para ver las métricas" />
      ) : loading ? (
        <PujaSpinner size="lg" />
      ) : !datos ? null : (
        <>
          {/* El detalle por animal ya venía recortado a lo propio, pero nada
              en pantalla se lo decía al invitado: veía los mismos gráficos
              que el titular sin ninguna aclaración de qué es agregado del
              remate completo y qué es suyo. No se muestra con nivel 'nada'
              porque ahí la request ni siquiera llega a tener éxito (403). */}
          {!datos.es_titular && AVISO_INVITADO[datos.analytics_compartidas] && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={AVISO_INVITADO[datos.analytics_compartidas](
                datos.nombre_organizador || 'el organizador'
              )}
            />
          )}
          {/* Row 1 — KPI Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card variant="borderless" style={{ background: token.colorPrimaryBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Visitas totales"
                  value={datos.visitas_totales}
                  prefix={<EyeOutlined />}
                  valueStyle={{ color: token.colorPrimary, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card variant="borderless" style={{ background: token.colorSuccessBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Visitas hoy"
                  value={datos.visitas_hoy}
                  prefix={<RiseOutlined />}
                  valueStyle={{ color: token.colorSuccess, fontWeight: 700 }}
                />
              </Card>
            </Col>
            {/* Navegadores distintos (id anónimo, utils/visitante.js). "Hoy" es
                el día argentino; el total de siempre va como sufijo del número
                —no en un renglón aparte, que hacía más alta esta tarjeta que
                las demás— y es el que sale en el informe PDF. El título es
                "Visitantes" y no "Visitantes únicos" por lo mismo: en una
                línea, como las otras cinco. */}
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card variant="borderless" style={{ background: token.colorSuccessBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Visitantes hoy"
                  value={datos.visitantes_unicos_hoy}
                  prefix={<UserOutlined />}
                  suffix={
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      · {Number(datos.visitantes_unicos_totales || 0).toLocaleString('es-AR')} en total
                    </Text>
                  }
                  valueStyle={{ color: token.colorSuccess, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card variant="borderless" style={{ background: token.colorInfoBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Compartidos"
                  value={datos.compartidos_totales}
                  prefix={<ShareAltOutlined />}
                  valueStyle={{ color: token.colorInfo, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card variant="borderless" style={{ background: token.colorWarningBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Total ofertas"
                  value={datos.total_ofertas}
                  prefix={<ShoppingOutlined />}
                  valueStyle={{ color: token.colorWarning, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card variant="borderless" style={{ borderRadius: token.borderRadiusLG }}>
                {datos.oferta_mas_alta ? (
                  <Statistic
                    title={
                      <span>
                        <TrophyOutlined style={{ color: '#faad14', marginRight: 4 }} />
                        Oferta más alta
                      </span>
                    }
                    value={datos.oferta_mas_alta.monto}
                    prefix="$"
                    suffix={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {datos.oferta_mas_alta.nom_animal}
                      </Text>
                    }
                    valueStyle={{ color: '#52c41a', fontWeight: 700, fontSize: 22 }}
                    formatter={(v) => Number(v).toLocaleString('es-AR')}
                  />
                ) : (
                  <Statistic title="Oferta más alta" value="-" />
                )}
              </Card>
            </Col>
          </Row>

          {/* Row 2 — Gráfico de visitas por día */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card
                title={`Visitas por día${dias === 0 ? '' : ` (últimos ${dias} días)`}`}
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <LineChart data={datos.visitas_por_dia} />
              </Card>
            </Col>
          </Row>

          {/* Row 3 — Top animales por visitas / compartidos */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <Card
                title="Top animales por visitas"
                extra={<VerCompletoBtn tipo="visitas" eventoId={eventoId} nombreEvento={nombreEventoActual} navigate={navigate} />}
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.visitas_por_animal}
                  columns={colsAnimalVisitas}
                  rowKey="clave"
                  locale={{ emptyText: 'Sin visitas registradas' }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title="Top animales por compartidos"
                extra={<VerCompletoBtn tipo="compartidos" eventoId={eventoId} nombreEvento={nombreEventoActual} navigate={navigate} />}
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.compartidos_por_animal}
                  columns={colsAnimalCompartidos}
                  rowKey="id_animal"
                  locale={{ emptyText: 'Sin compartidos registrados' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Row 4 — Ofertas por animal / categoría */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <Card
                title="Ofertas por animal"
                extra={<VerCompletoBtn tipo="ofertas_animal" eventoId={eventoId} nombreEvento={nombreEventoActual} navigate={navigate} />}
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.ofertas_por_animal}
                  columns={colsOfertasAnimal}
                  rowKey="id_animal"
                  locale={{ emptyText: 'Sin ofertas registradas' }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title="Ofertas por categoría"
                extra={<VerCompletoBtn tipo="ofertas_categoria" eventoId={eventoId} nombreEvento={nombreEventoActual} navigate={navigate} />}
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.ofertas_por_categoria}
                  columns={colsOfertasCategoria}
                  rowKey="nom_categoria"
                  locale={{ emptyText: 'Sin ofertas registradas' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Row 5 — Ofertas por corral */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card
                title="Ofertas por corral"
                extra={<VerCompletoBtn tipo="ofertas_corral" eventoId={eventoId} nombreEvento={nombreEventoActual} navigate={navigate} />}
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.ofertas_por_corral}
                  columns={colsOfertasCorral}
                  rowKey="corral"
                  locale={{ emptyText: 'Sin ofertas por corral' }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Analytics;
